<template>
  <div class="mb-2">
    <div class="d-flex align-center ga-1">
      <SoButton
        :title="file.filename"
        prepend-icon="external-link"
        type="secondary"
        :loading="isOpening"
        :disabled="fileQuery.isFetching"
        data-test-id="junifer-comm-document-open"
        @click="open"
      />
      <SoTooltip text="Download">
        <SoButton
          icon="download"
          type="icon"
          :aria-label="`Download ${file.filename}`"
          :loading="isDownloading"
          :disabled="fileQuery.isFetching"
          data-test-id="junifer-comm-document-download"
          @click="download"
        />
      </SoTooltip>
    </div>
    <SoAlert
      v-if="failedAction"
      type="error"
      class="mt-2"
      data-testid="junifer-comm-document-error"
    >
      <div class="d-flex align-center justify-space-between ga-4">
        Couldn't open {{ file.filename }}.
        <SoButton
          title="Retry"
          data-test-id="junifer-comm-document-retry"
          @click="retry"
        />
      </div>
    </SoAlert>
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoTooltip from "@/components/SoTooltip.vue"
  import { JuniferService } from "@/services/JuniferService"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { IJuniferFile } from "@/models/juniferCommunication/juniferComm.parser"
  import { downloadFile, openBlobInTab } from "@/helpers/downloadFile"

  type DocumentAction = "open" | "download"

  export default defineComponent({
    components: { SoAlert, SoButton, SoTooltip },
    props: {
      communicationId: {
        type: Number,
        required: true,
      },
      file: {
        type: Object as PropType<IJuniferFile>,
        required: true,
      },
    },
    data() {
      return {
        fileQuery: JuniferService.getCommunicationFile(
          useCurrentAccountStore().coreId.data,
          this.communicationId,
          this.file.id,
        ),
        activeAction: null as DocumentAction | null,
        failedAction: null as DocumentAction | null,
      }
    },
    computed: {
      isOpening(): boolean {
        return this.activeAction === "open"
      },
      isDownloading(): boolean {
        return this.activeAction === "download"
      },
    },
    methods: {
      async fetchPdf(action: DocumentAction): Promise<Blob | null> {
        this.activeAction = action
        this.failedAction = null
        const result = await this.fileQuery.refetch()
        this.activeAction = null
        if (result.isError || !(result.data instanceof Blob)) {
          this.failedAction = action
          return null
        }
        return result.data
      },
      async open() {
        // Opened during the click itself, so a popup blocker doesn't stop a
        // tab opened only after a fetch that can take several seconds.
        const tab = window.open("", "_blank")
        const pdf = await this.fetchPdf("open")
        if (!pdf) {
          tab?.close()
          return
        }
        // Shown as a PDF whatever the response claimed, so nothing else can
        // render under Nova's origin.
        if (tab)
          openBlobInTab(tab, new Blob([pdf], { type: "application/pdf" }))
        else downloadFile(pdf, this.file.filename, "application/pdf")
      },
      async download() {
        const pdf = await this.fetchPdf("download")
        if (pdf) downloadFile(pdf, this.file.filename, "application/pdf")
      },
      retry() {
        if (this.failedAction === "open") this.open()
        else this.download()
      },
    },
  })
</script>
